import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db, hasConfig } from '../auth/firebaseClient';
import { getCached, setCached, clearCache } from './cacheService';

const normalize = (value) => String(value || '').trim().toLowerCase();

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value?.toMillis) return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

let jsonUsers = null;

const loadJsonUsers = () => {
  if (jsonUsers) return jsonUsers;

  try {
    const usersData = require('../data/users.json');
    const users = usersData.users || [];

    jsonUsers = users.map(user => ({
      id: user.id,
      uid: user.uid,
      email: user.email,
      name: user.name,
      role: user.role,
      profileId: user.profileId,
      createdAt: new Date(user.createdAt).getTime(),
      lastLoginAt: user.lastLoginAt ? new Date(user.lastLoginAt).getTime() : null,
      source: 'json',
    }));

    console.log('[UserService] Loaded', jsonUsers.length, 'users from JSON');
    return jsonUsers;
  } catch (e) {
    console.error('[UserService] Error loading JSON users:', e);
    return [];
  }
};

export const getAllUsers = async (options = {}) => {
  const { limit: limitCount = 100, role, forceRefresh = false } = options;
  const cacheKey = `users_${role || 'all'}`;

  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[UserService] Returning cached users:', cached.length);
      return cached;
    }
  }

  const users = loadJsonUsers();

  let filtered = users;
  if (role) {
    filtered = users.filter(u => u.role === role);
  }

  if (limitCount) {
    filtered = filtered.slice(0, limitCount);
  }

  setCached(cacheKey, filtered);
  console.log('[UserService] Returning', filtered.length, 'users from JSON');

  return filtered;
};

export const getUserById = async (uid) => {
  const users = loadJsonUsers();
  return users.find(u => u.uid === uid || u.id === uid) || null;
};

export const getUserByEmail = async (email) => {
  const users = loadJsonUsers();
  const normalized = normalize(email);
  return users.find(u => normalize(u.email) === normalized) || null;
};

export const createUser = async (userData) => {
  if (!hasConfig || !db || !userData.uid) {
    throw new Error('uid is required');
  }
  
  const normalizedEmail = userData.email ? normalize(userData.email) : null;
  
  await setDoc(doc(db, 'users', userData.uid), {
    ...userData,
    email: normalizedEmail,
    role: userData.role || 'invitee',
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
    loginCount: 0,
    devices: [],
    accessLog: userData.accessLog || [],
  }, { merge: true });
  
  return { id: userData.uid, ...userData };
};

export const updateUser = async (uid, updates) => {
  if (!hasConfig || !db || !uid) return;
  
  const updateData = { ...updates, updatedAt: serverTimestamp() };
  
  if (updates.email) {
    updateData.email = normalize(updates.email);
  }
  
  await updateDoc(doc(db, 'users', uid), updateData);
};

export const deleteUser = async (uid) => {
  if (!hasConfig || !db || !uid) return;
  await deleteDoc(doc(db, 'users', uid));
};

export const trackUserLogin = async (uid, deviceInfo = {}) => {
  if (!hasConfig || !db || !uid) return;
  
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) return;
  
  const currentData = userDoc.data();
  const currentDevices = currentData.devices || [];
  const loginCount = (currentData.loginCount || 0) + 1;
  
  const newDevice = deviceInfo.type || 'unknown';
  const updatedDevices = currentDevices.includes(newDevice) 
    ? currentDevices 
    : [...currentDevices, newDevice];
  
  await updateDoc(doc(db, 'users', uid), {
    lastLoginAt: serverTimestamp(),
    loginCount,
    devices: updatedDevices,
  });
};

export const updateUserAccess = async (uid, albumId, accessData = {}) => {
  if (!hasConfig || !db || !uid || !albumId) return;
  
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) return;
  
  const currentData = userDoc.data();
  const accessLog = currentData.accessLog || [];
  const existingIndex = accessLog.findIndex(a => a.albumId === albumId);
  
  const now = serverTimestamp();
  
  if (existingIndex >= 0) {
    accessLog[existingIndex] = {
      ...accessLog[existingIndex],
      ...accessData,
      lastAccessAt: now,
      viewCount: (accessLog[existingIndex].viewCount || 0) + 1,
    };
  } else {
    accessLog.push({
      albumId,
      firstAccessAt: now,
      lastAccessAt: now,
      viewCount: 1,
      ...accessData,
    });
  }
  
  await updateDoc(doc(db, 'users', uid), { accessLog });
};

export const getUserStats = async (forceRefresh = false) => {
  const cacheKey = 'user_stats';
  
  // Check cache first
  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) {
      console.log('[UserService] Returning cached stats');
      return cached;
    }
  }
  
  if (!hasConfig || !db) {
    return { total: 0, active: 0, byRole: {} };
  }
  
  console.log('[UserService] Fetching user stats from Firestore...');
  
  const snapshot = await getDocs(usersRef());
  const users = snapshot.docs.map(doc => doc.data());
  
  const byRole = {};
  let active = 0;
  
  users.forEach(user => {
    const role = user.role || 'invitee';
    byRole[role] = (byRole[role] || 0) + 1;
    
    const lastLogin = toMillis(user.lastLoginAt);
    if (lastLogin && Date.now() - lastLogin < 30 * 24 * 60 * 60 * 1000) {
      active++;
    }
  });
  
  const stats = {
    total: users.length,
    active,
    byRole,
  };
  
  setCached(cacheKey, stats);
  return stats;
};

export const clearUserCache = () => {
  clearCache('users_all');
  clearCache('users_admin');
  clearCache('users_editor');
  clearCache('users_creator');
  clearCache('users_invitee');
  clearCache('user_stats');
  console.log('[UserService] User cache cleared');
};